import type { InitOptions } from 'i18next';
import {
  DEFAULT_NS,
  FALLBACK_LOCALE,
  NAMESPACES,
  initialResources,
  SUPPORTED_LOCALES,
} from '@kidshome/i18n/resources';

/** localStorage key for the persisted locale override (mirrors `User.locale` after auth). */
export const LOCALE_STORAGE_KEY = 'kidshome.locale';

/**
 * Shared i18next init options (ADR-0025). The app calls `i18n.use(...).init(baseI18nOptions)`,
 * adding the LanguageDetector + initReactI18next. Catalogs are bundled here for the foundation
 * namespaces; feature namespaces load lazily per route in Phases 5–6.
 *
 * Interpolation does NOT escape (React already escapes); all number/date formatting goes through
 * `format.ts` (never i18next interpolation `format`), so no formatter is registered here.
 */
export const baseI18nOptions: InitOptions = {
  resources: initialResources,
  partialBundledLanguages: true,
  supportedLngs: [...SUPPORTED_LOCALES],
  fallbackLng: FALLBACK_LOCALE,
  defaultNS: DEFAULT_NS,
  ns: [...NAMESPACES],
  interpolation: { escapeValue: false },
  returnNull: false,
  detection: {
    order: ['localStorage', 'navigator'],
    lookupLocalStorage: LOCALE_STORAGE_KEY,
    caches: ['localStorage'],
  },
};

/** RTL locale list (none at launch; en/uk are LTR). Kept so `dir` wiring is ready (ADR-0025). */
const RTL_LOCALES = new Set<string>([]);

/** Document direction for a locale. */
export function dirForLocale(locale: string): 'ltr' | 'rtl' {
  return RTL_LOCALES.has(locale.split('-')[0] ?? locale) ? 'rtl' : 'ltr';
}
